import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, RefreshCw, Shield } from "lucide-react";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { BACKEND_URL } from "@/config";
import { buildApiCandidates, shouldFailoverBackendRequest } from "@/backendBases";
import SecuritySettingsCard from "@/components/tabs/SecuritySettingsCard";
import ActiveSessionsCard from "@/components/tabs/ActiveSessionsCard";
import LockedAccountsCard from "@/components/tabs/LockedAccountsCard";

const API = BACKEND_URL;

const ACTION_LABELS = {
  login_auto_locked: "Hatalı Giriş Nedeniyle Otomatik Kilit",
  password_recovered_via_parola: "Şifre kurtarma (parola, eski)",
  forgot_password_sms_sent: "Şifrem unuttum (SMS kodu)",
  credential_setup_completed: "İlk kurulum tamamlandı",
  temporary_login_code_issued: "Tek kullanımlık kod üretildi",
  password_updated: "Şifre güncellendi",
};

const methodLabel = (meta) => {
  const m = meta?.login_method;
  if (m === "temporary_code") return "Geçici kod";
  if (m === "password") return "Şifre";
  return "—";
};

export default function LoginManagementTab({ isVisible }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [actionFilter, setActionFilter] = useState("login_auto_locked");
  const [cleanupMode, setCleanupMode] = useState("date_range");
  const [cleanupConfirmText, setCleanupConfirmText] = useState("");

  const fetchLogs = async () => {
    setLoading(true);
    setErrorText("");
    try {
      const params = new URLSearchParams();
      params.set("limit", "500");
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      if (actionFilter.trim()) params.set("action", actionFilter.trim());

      let lastErr = null;
      let res = null;
      for (const base of buildApiCandidates(API)) {
        const url = base ? `${base}/api/login-management/logs?${params}` : `/api/login-management/logs?${params}`;
        try {
          res = await axios.get(url, { timeout: 25000 });
          break;
        } catch (err) {
          lastErr = err;
          if (shouldFailoverBackendRequest(err)) continue;
          const d = err.response?.data?.detail;
          throw new Error(typeof d === "string" ? d : err.message || "Liste alınamadı");
        }
      }
      if (!res) {
        const d = lastErr?.response?.data?.detail;
        throw new Error(typeof d === "string" ? d : lastErr?.message || "Bağlantı hatası");
      }
      const primaryItems = Array.isArray(res?.data?.items) ? res.data.items : [];
      if (primaryItems.length > 0) {
        setLogs(primaryItems);
        return;
      }

      // Geriye dönük uyumluluk: eski backend hâlâ audit-logs üzerinde olabilir.
      const fallbackParams = new URLSearchParams();
      fallbackParams.set("entity_type", "auth");
      fallbackParams.set("limit", "500");
      if (startDate) fallbackParams.set("start_date", startDate);
      if (endDate) fallbackParams.set("end_date", endDate);
      if (actionFilter.trim()) fallbackParams.set("action", actionFilter.trim());
      let fallbackRes = null;
      let fallbackErr = null;
      for (const base of buildApiCandidates(API)) {
        const fallbackUrl = base
          ? `${base}/api/audit-logs?${fallbackParams}`
          : `/api/audit-logs?${fallbackParams}`;
        try {
          fallbackRes = await axios.get(fallbackUrl, { timeout: 25000 });
          break;
        } catch (err) {
          fallbackErr = err;
          if (shouldFailoverBackendRequest(err)) continue;
          break;
        }
      }
      const fallbackItems = Array.isArray(fallbackRes?.data?.items) ? fallbackRes.data.items : [];
      if (fallbackItems.length > 0) {
        setLogs(fallbackItems);
      } else {
        // Her iki kaynak da boşsa temiz boş liste göster.
        setLogs([]);
        if (fallbackErr && !shouldFailoverBackendRequest(fallbackErr)) {
          const d = fallbackErr.response?.data?.detail;
          if (typeof d === "string") setErrorText(d);
        }
      }
    } catch (e) {
      setErrorText(e.message || "Kayıtlar yüklenemedi.");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible) fetchLogs();
  }, [isVisible]);

  const rows = useMemo(() => {
    return [...logs].sort(
      (a, b) =>
        String(b.created_at || "").localeCompare(String(a.created_at || ""))
    );
  }, [logs]);

  const cleanAuthLogs = async () => {
    setCleaning(true);
    setErrorText("");
    try {
      const payload = {
        mode: cleanupMode,
        confirm_text: cleanupConfirmText,
      };
      if (cleanupMode === "date_range") {
        payload.start_date = startDate;
        payload.end_date = endDate;
      }

      let lastErr = null;
      let res = null;
      for (const base of buildApiCandidates(API)) {
        const url = base ? `${base}/api/login-management/logs` : "/api/login-management/logs";
        try {
          res = await axios.delete(url, { data: payload, timeout: 30000 });
          break;
        } catch (err) {
          lastErr = err;
          if (shouldFailoverBackendRequest(err)) continue;
          const d = err.response?.data?.detail;
          throw new Error(typeof d === "string" ? d : err.message || "Temizleme yapılamadı");
        }
      }
      if (!res) {
        const d = lastErr?.response?.data?.detail;
        throw new Error(typeof d === "string" ? d : lastErr?.message || "Bağlantı hatası");
      }
      await fetchLogs();
    } catch (e) {
      setErrorText(e.message || "Kayıtlar temizlenemedi.");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Profesyonel oturum yönetimi: admin'in dinamik ayarları */}
      <SecuritySettingsCard />

      {/* Kilitli hesaplar: tek panelden kilit aç + geçici kod üret */}
      <LockedAccountsCard />

      {/* Aktif oturumlar tablosu (revoke / zorla logout) */}
      <ActiveSessionsCard />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-green-700" />
            Sisteme Giriş Yönetimi
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Kötüye kullanım odaklı görünüm: 3 hatalı giriş sonrası otomatik kilitlenen hesapları gösterir.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="w-[320px]">
              <Label className="text-xs">Tarih Aralığı</Label>
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={({ start, end }) => {
                  setStartDate(start);
                  setEndDate(end);
                }}
                placeholder="Tarih aralığı"
              />
            </div>
            <div>
              <Label className="text-xs">İşlem (action)</Label>
              <Input
                placeholder="örn. login_auto_locked"
                value={actionFilter}
                onChange={(e) => setActionFilter(e.target.value)}
                className="w-[200px]"
              />
            </div>
            <Button type="button" onClick={fetchLogs} disabled={loading}>
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              <span className="ml-2">Yenile</span>
            </Button>
          </div>
          <div className="border rounded-lg p-3 bg-red-50">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
              <div>
                <Label className="text-xs">Temizleme Yöntemi</Label>
                <SearchableSelect
                  className="mt-1 w-full"
                  value={cleanupMode}
                  onChange={(v) => setCleanupMode(v)}
                  showAllOption={false}
                  placeholder="Seçin"
                  searchPlaceholder="Ara..."
                  options={[
                    { id: "date_range", name: "Seçili tarih aralığını temizle" },
                    { id: "all", name: "Tüm giriş/kimlik kayıtlarını temizle (yalnız Süper Admin)" },
                  ]}
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Güvenlik Onayı (tümünü silmek için EMINIM yaz)</Label>
                <Input
                  value={cleanupConfirmText}
                  onChange={(e) => setCleanupConfirmText(e.target.value)}
                  placeholder="tümünü silmek için: EMINIM"
                />
              </div>
              <div className="md:col-span-2">
                <Button
                  type="button"
                  variant="destructive"
                  onClick={cleanAuthLogs}
                  disabled={cleaning}
                >
                  {cleaning ? "Temizleniyor..." : "Sisteme Giriş Yönetimi Kayıtlarını Temizle"}
                </Button>
              </div>
            </div>
          </div>
          {errorText && (
            <div className="text-sm text-red-600 border border-red-200 rounded p-2 bg-red-50">
              {errorText}
            </div>
          )}
          <div className="rounded-md border max-h-[min(70vh,560px)] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Tarih</TableHead>
                  <TableHead>İşlem</TableHead>
                  <TableHead>Hedef</TableHead>
                  <TableHead>Yöntem / Ek</TableHead>
                  <TableHead>İşlemi yapan</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                      Kayıt yok veya filtre sonucu boş.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((log) => (
                    <TableRow key={log.id || log.created_at}>
                      <TableCell className="text-xs whitespace-nowrap">
                        {log.created_at
                          ? new Date(log.created_at).toLocaleString("tr-TR")
                          : "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {ACTION_LABELS[log.action] || log.action || "—"}
                      </TableCell>
                      <TableCell className="text-sm">
                        {(log.target?.name || log.actor?.name || "—") +
                          (log.target?.id
                            ? ` (${String(log.target.id).slice(0, 8)}…)`
                            : "")}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {methodLabel(log.metadata)}
                        {log.metadata?.expires_at
                          ? ` · Son: ${new Date(log.metadata.expires_at).toLocaleString("tr-TR")}`
                          : ""}
                      </TableCell>
                      <TableCell className="text-xs">
                        {log.actor?.name || "—"}{" "}
                        {log.actor?.role ? `(${log.actor.role})` : ""}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
